/*
 * File: AddDeliveryAddressModal.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 Jan 2022 10:51:18 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { PlusOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Input, Modal, notification, Row, Select, Switch, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { QueryErrorModel } from 'models/ErrorModel';
import { SparingRequestPayload } from 'models/SparingRequests';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCreateAddressMutation } from 'redux/services/chuckieSue/addressesApi';
import { CountryMetadata, SubdivisionMetadata } from 'redux/services/chuckieSue/models/addresses';
import { setFullOutageAddress, setFullShippingAddress } from 'redux/slices/formSlice';
import { DeliveryAddressCountrySelector } from './DeliveryAddressCountrySelector';
import { DeliveryAddressStateSelector } from './DeliveryAddressStateSelector';
import { GoogleAutoComplete } from './GoogleAutoComplete';

/* ****************** Interfaces ****************** */
interface Props {
  show: boolean;
  onClose: () => void;
  addressTypes: string[];
  isShipping?: boolean;
}

interface FormModel {
  city: string;
  name: string;
  state: string;
  street: string;
  zipCode: string;
  code: string;
  addressType: string[];
  subdivision: SubdivisionMetadata;
  country: CountryMetadata;
  countryShortName: string;
}

const formItemLayout = {
  labelCol: {
    span: 24
  },
  wrapperCol: {
    span: 24
  }
};

export const AddDeliveryAddressModal = ({ show, onClose, addressTypes, isShipping }: Props): JSX.Element => {
  const { setFieldValue } = useFormikContext<SparingRequestPayload>();

  const dispatch = useDispatch();
  const [localityMetaData, setLocalityMetaData] = useState<{ country: CountryMetadata; subdivision: SubdivisionMetadata }>({
    country: {
      alpha2Code: '',
      alpha3Code: '',
      fullName: '',
      shortName: ''
    },
    subdivision: {
      categoryName: '',
      localCode: '',
      name: ''
    }
  });

  const [override, setOverride] = useState(false);

  const [createAddress, { isLoading }] = useCreateAddressMutation();

  const [form] = Form.useForm<FormModel>();

  const handleFormSubmit = async (formValue: FormModel): Promise<void> => {
    const payload = {
      city: formValue.city,
      subdivisionLocalCode: formValue.state,
      street1: formValue.street,
      postalCode: formValue.zipCode,
      name: formValue.name,
      addressType: formValue.addressType,
      code: formValue.code,
      subdivision: localityMetaData.subdivision,
      country: localityMetaData.country
    };

    try {
      const data = await createAddress({
        payload,
        skipValidation: false
      }).unwrap();

      if (isShipping) {
        setFieldValue('shippingLocationId', data.id);
        dispatch(
          setFullShippingAddress({
            addressType: data.addressType,
            city: data.city,
            code: data.code,
            country: data.country.fullName,
            countryAlpha2Code: data.country.alpha2Code,
            id: data.id,
            latitude: data.latitude,
            longitude: data.longitude,
            name: data.name,
            postalCode: data.postalCode,
            subdivisionLocalCode: data?.subdivision?.localCode ?? '',
            street1: data.street1,
            street2: data.street2,
            subdivision: data?.subdivision?.name ?? ''
          })
        );
      } else {
        setFieldValue('outageLocationId', data.id);
        dispatch(
          setFullOutageAddress({
            addressType: data.addressType,
            city: data.city,
            code: data.code,
            country: data.country.fullName,
            countryAlpha2Code: data.country.alpha2Code,
            id: data.id,
            latitude: data.latitude,
            longitude: data.longitude,
            name: data.name,
            postalCode: data.postalCode,
            subdivisionLocalCode: data?.subdivision?.localCode ?? '',
            street1: data.street1,
            street2: data.street2,
            subdivision: data?.subdivision?.name ?? ''
          })
        );
        // setFieldValue('fullInstallAddress', data);
      }
      form.resetFields();
      onClose();
    } catch (err) {
      const error = err as QueryErrorModel;

      notification.error({
        message: 'Error',
        description: `${error.data.errorMessage}`,
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 10
      });
    }
  };

  return (
    <Modal
      getContainer={false}
      centered={true}
      title="Add New Address"
      open={show}
      afterClose={(): void => form.resetFields()}
      confirmLoading={isLoading}
      onCancel={onClose}
      onOk={(): void => form.submit()}
      okText="Add Address"
      okButtonProps={{ icon: <PlusOutlined /> }}>
      <Form
        {...formItemLayout}
        layout="horizontal"
        form={form}
        initialValues={{
          city: '',
          countryShortName: 'US',
          name: '',
          code: '',
          addressType: [],
          state: '',
          street: '',
          suiteNumber: '',
          zipCode: '',
          subdivision: {
            categoryName: 'State',
            code: '',
            localCode: '',
            name: ''
          },
          country: {
            alpha2Code: 'US',
            alpha3Code: '',
            fullName: 'United States',
            shortName: 'United States'
          }
        }}
        onFinish={handleFormSubmit}>
        <Form.Item label="Address Autocomplete">
          <GoogleAutoComplete setLocalityMetaData={setLocalityMetaData} setAddressValues={form.setFieldValue} />
        </Form.Item>

        <Divider style={{ marginTop: 10, marginBottom: 10 }} />

        <Row justify="end" align="middle" gutter={2}>
          <Col>
            <Typography.Text>Override</Typography.Text>
          </Col>
          <Col>
            <Switch onChange={(checked): void => setOverride(checked)} size="small" />
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 5 }} label="Name" name="name" rules={[{ required: true, message: 'Please enter a name.' }]}>
          <Input />
        </Form.Item>
        <Form.Item style={{ marginBottom: 5 }} label="Address Type" name="addressType" rules={[{ required: true, message: 'Please select an address type.' }]}>
          <Select allowClear options={addressTypes.map((type) => ({ label: type, value: type }))} mode="multiple" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 5 }} label="Location Code" name="code" rules={[{ required: true, message: 'Please enter a location code.' }]}>
          <Input />
        </Form.Item>
        <Form.Item style={{ marginBottom: 5 }} label="Street" name="street" rules={[{ required: true, message: 'Please enter a street.' }]}>
          <Input disabled={!override} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 5 }} label="Suite, Unit, Building" name="suiteNumber">
          <Input />
        </Form.Item>

        <Form.Item style={{ marginBottom: 5 }} label="City" name="city" rules={[{ required: true, message: 'Please enter a city.' }]}>
          <Input disabled={!override} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 5 }} label="State" name="state">
          <DeliveryAddressStateSelector disabled={!override} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 5 }} label="Zip Code" name="zipCode" rules={[{ required: true, message: 'Please enter a zip code.' }]}>
          <Input disabled={!override} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 5 }} label="Country" name="countryShortName" rules={[{ required: true, message: 'Please Select a country.' }]}>
          <DeliveryAddressCountrySelector disabled={!override} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
